// Client side of the Phase 2 counter: callable wrappers and scoped, limited watchers over the
// operational collections (customers, shipments, boxes, empty-box stock). Clients never write
// these documents; every change is a command (04). Every query has a `where` on scope and a `limit`
// (02 §2.3). The composite indexes each watcher needs are listed in its comment.
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore';
import { httpsCallable, type Functions } from 'firebase/functions';
import type { Box } from '@ecobox/domain';
import type {
  ConsigneeDoc,
  CustomerDoc,
  NumberLease,
  ShipmentDoc,
  boxDocExtraSchema,
  emptyBoxStockDocSchema,
  stockMovementDocSchema,
  upsertCustomerInputSchema,
} from '@ecobox/schema';
import { toConfigError } from './config';
import { toDoc, watchOne, watchRows, type Unsubscribe } from './docs';
import type { LeaseRequest } from './leases';

// ---- callables ---------------------------------------------------------------

async function call<I, O>(functions: Functions, name: string, data: I): Promise<O> {
  try {
    return (await httpsCallable<I, O>(functions, name)(data)).data;
  } catch (err) {
    throw toConfigError(err);
  }
}

export type UpsertCustomerInput = (typeof upsertCustomerInputSchema)['_input'];

/** Creates or updates a customer. Online only. `existed` is true when a customer with that phone was already there. */
export function upsertCustomer(
  functions: Functions,
  input: UpsertCustomerInput,
): Promise<{ id: string; existed: boolean }> {
  return call(functions, 'upsertCustomer', input);
}

/** Asks the server for a range of tracking or receipt numbers for this device. Online only. */
export function leaseNumbers(functions: Functions, input: LeaseRequest): Promise<NumberLease> {
  return call(functions, 'leaseNumbers', input);
}

// ---- row types ---------------------------------------------------------------

export type ShipmentRow = ShipmentDoc;
export type CustomerRow = CustomerDoc;
export type ConsigneeRow = ConsigneeDoc;
export type BoxRow = Box &
  (typeof boxDocExtraSchema)['_output'] & {
    /** The shipment's tracking number (the domain Box calls it `shipmentId`). */
    trackingNo: string;
  };
export type EmptyBoxStockRow = (typeof emptyBoxStockDocSchema)['_output'];
export type StockMovementRow = (typeof stockMovementDocSchema)['_output'];

export const SHIPMENT_LIST_LIMIT = 50;
export const SHIPMENT_LIST_MAX = 500;

const toShipment = (id: string, data: DocumentData): ShipmentRow =>
  toDoc<ShipmentRow>(id, data, 'trackingNo');
const toCustomer = (id: string, data: DocumentData): CustomerRow =>
  toDoc<CustomerRow>(id, data, 'id');
const toConsignee = (id: string, data: DocumentData): ConsigneeRow =>
  toDoc<ConsigneeRow>(id, data, 'id');
const toBox = (id: string, data: DocumentData): BoxRow => toDoc<BoxRow>(id, data, 'barcode');

// ---- shipments ---------------------------------------------------------------

/**
 * The branch's newest shipments. Index: `shipments (branchId asc, createdAt desc)`.
 * Filters on status, payment and service are applied on these rows by the screen.
 */
export function watchShipmentList(
  db: Firestore,
  args: {
    branchId: string;
    max?: number;
    /** A hub also lists what its satellites booked (`hubBranchId`, 2026-10-30). */
    withSatellites?: boolean;
  },
  onData: (rows: ShipmentRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const max = Math.min(args.max ?? SHIPMENT_LIST_LIMIT, SHIPMENT_LIST_MAX);
  const byField = (field: 'branchId' | 'hubBranchId') =>
    query(
      collection(db, 'shipments'),
      where(field, '==', args.branchId),
      orderBy('createdAt', 'desc'),
      limit(max),
    );
  if (!args.withSatellites) return watchRows(byField('branchId'), toShipment, onData, onError);
  // Two live lists, newest first together, at most `max` in all.
  let own: ShipmentRow[] | null = null;
  let theirs: ShipmentRow[] | null = null;
  const emit = (): void => {
    if (own === null || theirs === null) return;
    const byNo = new Map([...own, ...theirs].map((r) => [r.trackingNo, r]));
    onData([...byNo.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, max));
  };
  const stopOwn = watchRows(
    byField('branchId'),
    toShipment,
    (rows) => {
      own = rows;
      emit();
    },
    onError,
  );
  const stopTheirs = watchRows(
    byField('hubBranchId'),
    toShipment,
    (rows) => {
      theirs = rows;
      emit();
    },
    onError,
  );
  return () => {
    stopOwn();
    stopTheirs();
  };
}

/** One shipment, live (the drawer). */
export function watchShipment(
  db: Firestore,
  trackingNo: string,
  onData: (row: ShipmentRow | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchOne(doc(db, 'shipments', trackingNo), toShipment, onData, onError);
}

/** One shipment by tracking number, or null (also when the person may not read it). */
export async function getShipment(db: Firestore, trackingNo: string): Promise<ShipmentRow | null> {
  try {
    const snap = await getDoc(doc(db, 'shipments', trackingNo));
    return snap.exists() ? toShipment(snap.id, snap.data()) : null;
  } catch (err) {
    console.warn(`Could not read shipment ${trackingNo}.`, err);
    return null;
  }
}

/**
 * Shipments of this branch sent by, or addressed to, a phone number (E.164), newest first.
 * Both are equality filters, so no composite index is needed.
 */
export async function findShipmentsByPhone(
  db: Firestore,
  args: { branchId: string; phoneE164: string; max?: number },
): Promise<ShipmentRow[]> {
  const max = args.max ?? 20;
  const base = [collection(db, 'shipments'), where('branchId', '==', args.branchId)] as const;
  const [asShipper, asConsignee] = await Promise.all([
    getDocs(query(...base, where('shipper.phoneE164', '==', args.phoneE164), limit(max))),
    getDocs(
      query(...base, where('consignee.phones', 'array-contains', args.phoneE164), limit(max)),
    ),
  ]);
  const seen = new Map<string, ShipmentRow>();
  for (const d of [...asShipper.docs, ...asConsignee.docs])
    seen.set(d.id, toShipment(d.id, d.data()));
  return [...seen.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, max);
}

// ---- boxes -------------------------------------------------------------------

/** One box by barcode, or null. */
export async function getBox(db: Firestore, barcode: string): Promise<BoxRow | null> {
  try {
    const snap = await getDoc(doc(db, 'boxes', barcode));
    return snap.exists() ? toBox(snap.id, snap.data()) : null;
  } catch (err) {
    console.warn(`Could not read box ${barcode}.`, err);
    return null;
  }
}

/** The boxes of one shipment (at most 200, the most a command carries). Equality filters only. */
export function watchShipmentBoxes(
  db: Firestore,
  args: { branchId: string; trackingNo: string },
  onData: (rows: BoxRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'boxes'),
    where('branchId', '==', args.branchId),
    where('trackingNo', '==', args.trackingNo),
    limit(200),
  );
  return watchRows(q, toBox, onData, onError);
}

// ---- customers -----------------------------------------------------------------

/** The branch's most recently changed customers. Index: `customers (branchId asc, updatedAt desc)`. */
export function watchCustomerList(
  db: Firestore,
  args: { branchId: string; max?: number },
  onData: (rows: CustomerRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'customers'),
    where('branchId', '==', args.branchId),
    orderBy('updatedAt', 'desc'),
    limit(args.max ?? 50),
  );
  return watchRows(q, toCustomer, onData, onError);
}

/** Customers of this branch whose primary phone starts with `prefix`. Index: `customers (branchId asc, phoneE164 asc)`. */
export async function searchCustomersByPhone(
  db: Firestore,
  args: { branchId: string; prefix: string; max?: number },
): Promise<CustomerRow[]> {
  const snap = await getDocs(
    query(
      collection(db, 'customers'),
      where('branchId', '==', args.branchId),
      where('phoneE164', '>=', args.prefix),
      where('phoneE164', '<=', `${args.prefix}`),
      orderBy('phoneE164'),
      limit(args.max ?? 20),
    ),
  );
  return snap.docs.map((d) => toCustomer(d.id, d.data()));
}

/** Words with a capital first letter ("maria santos" → "Maria Santos"), the way names are usually stored. */
export function titleCase(text: string): string {
  return text.replace(
    /\p{L}[\p{L}'’-]*/gu,
    (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
  );
}

/**
 * Customers of this branch whose name starts with what was typed (as typed, or with capitals).
 * Index: `customers (branchId asc, name asc)`.
 */
export async function searchCustomersByName(
  db: Firestore,
  args: { branchId: string; term: string; max?: number },
): Promise<CustomerRow[]> {
  const term = args.term.trim();
  const variants = [...new Set([term, titleCase(term)])];
  const found = new Map<string, CustomerRow>();
  for (const v of variants) {
    const snap = await getDocs(
      query(
        collection(db, 'customers'),
        where('branchId', '==', args.branchId),
        where('name', '>=', v),
        where('name', '<=', `${v}`),
        orderBy('name'),
        limit(args.max ?? 20),
      ),
    );
    for (const d of snap.docs) found.set(d.id, toCustomer(d.id, d.data()));
  }
  return [...found.values()].slice(0, args.max ?? 20);
}

/** One customer, live. */
export function watchCustomer(
  db: Firestore,
  id: string,
  onData: (row: CustomerRow | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchOne(doc(db, 'customers', id), toCustomer, onData, onError);
}

/**
 * A customer's address book at one branch. The rules let staff read a consignee only inside their
 * scope, and a list query is allowed only when the query itself proves that, so it must filter on
 * `branchId` like every other list in this file (an unfiltered query is refused as a whole).
 */
export function watchConsignees(
  db: Firestore,
  args: { customerId: string; branchId: string },
  onData: (rows: ConsigneeRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'customers', args.customerId, 'consignees'),
    where('branchId', '==', args.branchId),
    limit(100),
  );
  return watchRows(q, toConsignee, onData, onError);
}

/** A customer's latest shipments at this branch. Index: `shipments (branchId asc, shipper.customerId asc, createdAt desc)`. */
export function watchCustomerShipments(
  db: Firestore,
  args: { branchId: string; customerId: string; max?: number },
  onData: (rows: ShipmentRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'shipments'),
    where('branchId', '==', args.branchId),
    where('shipper.customerId', '==', args.customerId),
    orderBy('createdAt', 'desc'),
    limit(args.max ?? 25),
  );
  return watchRows(q, toShipment, onData, onError);
}

// ---- empty boxes -----------------------------------------------------------------

/** Empty-box stock per size for a branch, live. Equality filter only. */
export function watchEmptyBoxStock(
  db: Firestore,
  branchId: string,
  onData: (rows: EmptyBoxStockRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, 'emptyBoxStock'), where('branchId', '==', branchId), limit(100));
  return watchRows(q, (id, data) => toDoc<EmptyBoxStockRow>(id, data), onData, onError);
}

/** The branch's latest stock movements. Index: `stockMovements (branchId asc, at desc)`. */
export function watchStockMovements(
  db: Firestore,
  args: { branchId: string; max?: number },
  onData: (rows: StockMovementRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'stockMovements'),
    where('branchId', '==', args.branchId),
    orderBy('at', 'desc'),
    limit(args.max ?? 50),
  );
  return watchRows(q, (id, data) => toDoc<StockMovementRow>(id, data, 'id'), onData, onError);
}
